import { v4 as uuidv4 } from 'uuid';
import { EventInput } from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';
import { FormikHelpers } from 'formik';
import { DateSelectArg, DatesSetArg, EventContentArg } from 'fullcalendar';
import { isEmpty } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { eAlertType } from '../../../../providers/ToastProvider/Toast.enum';
import { useToastContext } from '../../../../providers/ToastProvider/Toast.hook';
import { useUserContext } from '../../../../providers/UserProvider/User.context';
import { eUserRole } from '../../../../providers/UserProvider/User.enums';
import { useWorkspaceContext } from '../../../../providers/WorkspaceProvider/Workspace.context';
import { AgendaService } from '../../Agenda.service';
import { tTemplate } from '../../Agenda.types';
import { useAgendaContext } from '../../hooks/agenda.hook';
import { CalendarContext } from './Calendar.context';
import { eAgendaTabs, eCalendarView } from './Calendar.enums';
import {
  eventLegacyParser,
  formatTodayDate,
  getAllowedFields,
  buildUpdatedEventData,
  getInterval,
  mapEventsToResourceView,
  removeEmpty,
  removeFields,
  templateLegacyParser,
  tResourceMappableEvent,
} from './Calendar.functions';
import { CalendarService } from './Calendar.service';
import {
  tCalendarContext,
  tCreateEventForm,
  tCreateEventRequest,
  tUpdateEventForm,
  tUpdateEventRequest,
} from './Calendar.types';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../../../../lib/logging';

export const initialValues: tCreateEventForm = {
  templateId: '',
  title: '',
  startDate: new Date(),
  time: new Date(),
  description: '',
  duration: '',
  address: '',
  link: '',
  guests: [],
  note: '',
};

export function useCalendarContext(): tCalendarContext {
  return useContext(CalendarContext);
}

export default function useCalendar(): tCalendarContext {
  const { currentWorkspace } = useWorkspaceContext();
  const { selectedLead } = useAgendaContext();
  const { currentUser, currentUserRole } = useUserContext();
  const { addAlert } = useToastContext();
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();

  const calendarRef = useRef<FullCalendar | null>(null);
  const isFetched = useRef<boolean>(false);
  const [events, setEvents] = useState<EventInput[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  // initialLoading guards the very first paint so the user sees a skeleton instead of the
  // "Bienvenido a la Agenda" empty placeholder while templates are still loading. It flips
  // false once templates resolve; subsequent fetches via FullCalendar's datesSet are tracked
  // by `loading` (used by HeaderToolbar) without unmounting the calendar.
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  // hasFetchedEvents flips true after the first datesSet fetch resolves. The empty-state gate
  // uses it instead of relying solely on isEmpty(events): pre-fetch, events is naturally empty,
  // so a templates-only check would deadlock (gate hides FullCalendar → datesSet never fires).
  const [hasFetchedEvents, setHasFetchedEvents] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<eAgendaTabs>(eAgendaTabs.EVENTS);
  const [selectedEvent, setSelectedEvent] = useState<EventInput | undefined>(
    undefined,
  );
  const [selectedTemplate, setSelectedTemplate] = useState<tTemplate | null>(
    null,
  );
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [currentDateTitle, setCurrentDateTitle] = useState<string>('');
  const [isOpenDatepicker, setIsOpenDatepicker] = useState<boolean>(false);
  const [currentView, setCurrentView] = useState<eCalendarView>(
    eCalendarView.LIST_DAY,
  );
  const [showCreateEvent, setShowCreateEvent] = useState<boolean>(false);
  const [showUpdateEvent, setShowUpdateEvent] = useState<boolean>(false);
  const [showDeleteEvent, setShowDeleteEvent] = useState<boolean>(false);
  const [showDeleteTemplate, setShowDeleteTemplate] = useState<boolean>(false);
  const [templates, setTemplates] = useState<tTemplate[]>([]);
  const [currentArg, setCurrentArg] = useState<EventContentArg | null>(null);

  function isValidUUID(uuid: string | undefined): boolean {
    const isValid =
      !isEmpty(uuid) && uuid !== '00000000-0000-0000-0000-000000000000';

    return isValid;
  }

  useEffect(() => {
    const queryParam = searchParams.get('tab');
    const initialTab =
      queryParam === 'templates' ? eAgendaTabs.TEMPLATES : eAgendaTabs.EVENTS;

    setCurrentTab(initialTab);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      const calendarApi = calendarRef.current?.getApi();
      const calendarTitle =
        calendarApi?.view?.title || formatTodayDate(new Date());

      if (calendarApi) setCurrentDateTitle(calendarTitle);
    }, 1000);
  }, [calendarRef.current]);

  useEffect(() => {
    if (isEmpty(currentWorkspace) || isEmpty(currentWorkspace.id)) return;

    // Workspace changed — reset all per-workspace state (templates, events, render gates,
    // one-shot fetch guard). Without this reset, switching workspaces leaves stale templates
    // in event creation flows and locks the skeleton/empty-state gates with the prior
    // workspace's outcome.
    isFetched.current = false;
    setInitialLoading(true);
    setHasFetchedEvents(false);
    setEvents([]);
    setTemplates([]);

    const fetchTemplates = async () => {
      try {
        const response = await CalendarService.getTemplates(currentWorkspace.id);

        if (response)
          setTemplates(
            response.data.templates.map((template) =>
              templateLegacyParser(template, currentWorkspace.id),
            ),
          );
      } catch (err) {
        LLog.error(
          {
            component: LOG_COMPONENTS.UI,
            method: 'calendar',
            operation: LOG_OPERATIONS.UI_INTERACTION,
          },
          'Error fetching templates',
          err instanceof Error ? err : new Error(String(err)),
        );
      } finally {
        // Unblock the render gate regardless of templates outcome — workspaces with zero
        // templates still need to see the empty state, not a skeleton forever (BUG-262).
        setInitialLoading(false);
      }
    };

    if (!isFetched.current) {
      isFetched.current = true;
      // Events fetch is owned by FullCalendar's datesSet callback (onDatesSetUpdate).
      // The previous mount-time fetchEvents() ran in parallel and produced a "blank-then-
      // populate" flicker as the V2 datesSet response overwrote the legacy response.
      fetchTemplates();
    }
  }, [currentWorkspace?.id]);

  async function onEventClick(eventClickInfo: DateSelectArg) {
    const resourceId = eventClickInfo?.resource?.id || '1';
    const calendarApi = eventClickInfo.view.calendar;

    calendarApi.unselect(); // clear date selection

    const newEvent: EventInput = {
      id: uuidv4(),
      title: '(Sin título)',
      start: eventClickInfo.startStr,
      end: eventClickInfo.endStr,
      allDay: eventClickInfo.allDay,
      color: '#EEF4FF',
      data: initialValues,
      resourceId,
    };

    setEvents([...events, newEvent]);

    calendarApi.addEvent(newEvent);

    setSelectedEvent(newEvent);
    setShowCreateEvent(true);
  }

  function onNavigate(direction: 'prev' | 'next') {
    const calendarApi = calendarRef.current?.getApi();

    if (calendarApi) {
      calendarApi[direction]();
      setCurrentDateTitle(calendarApi.view.title);
      setCurrentDate(calendarApi.getDate());
    }
  }

  function onViewResourceTimeGridDay() {
    const calendarApi = calendarRef.current?.getApi();

    if (calendarApi) {
      calendarApi.today(); // Navega al día de hoy
      calendarApi.changeView('resourceTimeGridDay');
      setCurrentDate(calendarApi.getDate());
    }
  }

  function onToggleDatepicker() {
    setIsOpenDatepicker(!isOpenDatepicker);
  }

  function onChangeDatepicker(date: Date) {
    const calendarApi = calendarRef.current?.getApi();

    if (calendarApi) {
      calendarApi.gotoDate(date);

      setCurrentDate(date);
      setCurrentDateTitle(calendarApi.view.title);

      onToggleDatepicker();
    }
  }

  async function fetchEventsForRange(
    start: Date,
    end: Date,
    viewType: string,
  ): Promise<void> {
    const calendarApi = calendarRef.current?.getApi();

    if (!currentWorkspace || !currentUser) return;

    try {
      setLoading(true);
      const from = start.toISOString();
      const to = end.toISOString();

      const response = await CalendarService.getEventsByDate(
        currentWorkspace.id,
        from,
        to,
      );

      if (response) {
        let responseEvents = response.data.events;

        if (currentUserRole === eUserRole.SELLER) {
          // tEventResponseV2 names the creator field `creatorAuthID`; eventLegacyParser
          // below maps it to `user_id` for downstream consumers, but the filter runs on
          // the raw V2 shape.
          responseEvents = responseEvents.filter(
            (event) =>
              (event.guests && event.guests.includes(currentUser.authId)) ||
              event.creatorAuthID === currentUser.authId,
          );
        }

        const eventsFromResponse = responseEvents.map((event) =>
          eventLegacyParser(event, currentWorkspace.id),
        );
        const events: tResourceMappableEvent[] = eventsFromResponse.map((event) => ({
          id: event.id,
          title: event.name,
          start: event.start_date,
          end: event.end_date,
          allDay: false,
          color: '#EEF4FF',
          data: {
            id: event.id,
            templateId: event.template_id,
            title: event.name,
            startDate: new Date(event.start_date),
            time: new Date(event.start_date),
            description: event.description,
            duration: '00:30:00',
            address: event.address,
            link: event.links?.[0],
            guests: event.guests,
            note: event.note,
            user_id: event.user_id,
            related_entity_id: event.related_entity_id,
            lead: {
              id: isValidUUID(event.lead?.id) ? event.lead?.id : '',
              // No fabricated name here: an unresolved lead and an event with no
              // lead both arrive empty, and only the component can tell them
              // apart — with related_entity_id (BUG-1102).
              full_name: event.lead?.full_name,
              profile_picture: event.lead?.profile_picture || '',
            },
          },
        }));

        if (viewType === eCalendarView.RESOURCE_TIME_GRID_DAY) {
          calendarApi?.removeAllEvents();
          setEvents(mapEventsToResourceView(events));
        } else {
          setEvents(events);
        }
      }
    } catch (err) {
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'calendar',
          operation: LOG_OPERATIONS.UI_INTERACTION,
        },
        'Error in calendar operation',
        err instanceof Error ? err : new Error(String(err)),
      );
      addAlert({
        type: eAlertType.ERROR,
        message: t('agenda/agenda:events-fetch-error'),
      });
    }
    setLoading(false);
    setHasFetchedEvents(true);
  }

  async function onDatesSetUpdate(args: DatesSetArg) {
    await fetchEventsForRange(args.start, args.end, args.view.type);
  }

  // If currentUser resolves AFTER FullCalendar's initial datesSet fired and was
  // early-returned (the !currentUser guard inside fetchEventsForRange), no further
  // datesSet fires for the same range — manual navigation would otherwise be required
  // to recover. Trigger a fresh fetch using the current view range when currentUser
  // becomes available.
  useEffect(() => {
    if (!currentUser?.authId || hasFetchedEvents || initialLoading) return;
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;
    const view = calendarApi.view;
    fetchEventsForRange(view.activeStart, view.activeEnd, view.type);
    // fetchEventsForRange is recreated each render but reads the latest closure values;
    // including it in deps would loop. Tracking the inputs that should retrigger directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.authId, hasFetchedEvents, initialLoading]);

  async function changeView(view: eCalendarView) {
    const calendarApi = calendarRef.current?.getApi();

    if (calendarApi) {
      calendarApi.changeView(view);
      setCurrentDateTitle(calendarApi.view.title);
      setCurrentView(view);
    }
  }

  async function onCreateChatEventSubmit(
    event: React.MouseEvent<HTMLButtonElement>,
    values: tCreateEventForm,
  ) {
    try {
      event.preventDefault();
      const calendarApi = calendarRef.current?.getApi();
      const calendarEvent = calendarApi?.getEventById(selectedEvent?.id || '');

      const newEvent: EventInput = {
        id: selectedEvent?.id,
        title: values.title,
        color: '#EEF4FF',
        data: {
          ...values,
          guests: values.guests.map((guest) => guest.value),
        },
        allDay: false,
        resourceId: selectedEvent?.resourceId,
      };

      if (calendarEvent) {
        newEvent.start = calendarEvent.start?.toISOString() || '';
        newEvent.end = calendarEvent.end?.toISOString() || '';
        calendarEvent?.remove();
      } else {
        const formDate = getInterval({
          date: values.startDate.toISOString(),
          duration: values.duration,
          time: values.time.toISOString(),
        });

        newEvent.start = formDate.startDate;
        newEvent.end = formDate.endDate;
      }

      let createEventRequest: Partial<tCreateEventRequest> = {
        template_id: values.templateId,
        workspace_id: currentWorkspace?.id || '',
        name: values.title,
        start_date: newEvent.start,
        end_date: newEvent.end,
        user_id: currentUser?.authId,
        note: values.note,
        address: values.address,
        links: [values.link],
        description: values.description || '',
        guests: values.guests.map((guest) => guest.value),
      };

      const allowedFields = getAllowedFields(templates, values);
      if (!allowedFields.users) {
        createEventRequest.guests = [];
      }

      createEventRequest = removeEmpty(createEventRequest);
      createEventRequest = removeFields(createEventRequest, allowedFields);

      await AgendaService.createAgendaLeadEvent(
        createEventRequest,
        selectedLead?.uuid ?? '',
      );
    } catch (err) {
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'calendar',
          operation: LOG_OPERATIONS.UI_INTERACTION,
        },
        'Error in calendar operation',
        err instanceof Error ? err : new Error(String(err)),
      );
    }

    setShowCreateEvent(false);
  }

  async function onCreateEventSubmit(
    values: tCreateEventForm,
    formikHelpers: FormikHelpers<tCreateEventForm>,
  ) {
    const calendarApi = calendarRef.current?.getApi();
    const calendarEvent = calendarApi?.getEventById(selectedEvent?.id || '');

    const newEvent: EventInput = {
      id: selectedEvent?.id,
      title: values.title,
      color: '#EEF4FF',
      data: buildUpdatedEventData({
        values,
        guests: values.guests.map((guest) => guest.value),
        previousData: calendarEvent?.extendedProps?.data,
      }),
      allDay: false,
      resourceId: selectedEvent?.resourceId,
    };

    if (calendarEvent) {
      newEvent.start = calendarEvent.start?.toISOString() || '';
      newEvent.end = calendarEvent.end?.toISOString() || '';
      calendarEvent?.remove();
    } else {
      const formDate = getInterval({
        date: values.startDate.toISOString(),
        duration: values.duration,
        time: values.time.toISOString(),
      });

      newEvent.start = formDate.startDate;
      newEvent.end = formDate.endDate;
    }

    let createEventRequest: Partial<tCreateEventRequest> = {
      template_id: values.templateId,
      workspace_id: currentWorkspace?.id || '',
      name: values.title,
      start_date: newEvent.start,
      end_date: newEvent.end,
      user_id: currentUser?.authId,
      note: values.note,
      address: values.address,
      links: [values.link],
      description: values.description || '',
      guests: values.guests.map((guest) => guest.value),
    };

    const allowedFields = getAllowedFields(templates, values);
    if (!allowedFields.users) {
      createEventRequest.guests = [];
    }

    createEventRequest = removeEmpty(createEventRequest);
    createEventRequest = removeFields(createEventRequest, allowedFields);

    try {
      const response = await CalendarService.createEvent(createEventRequest);
      const request = response.data;
      newEvent.id = request?.id;
      newEvent.data.id = request?.id;
      newEvent.data.user_id = currentUser?.authId;
      if (!isEmpty(events)) {
        calendarApi?.addEvent(newEvent);
        setEvents([...events, newEvent]);
      } else {
        setEvents([newEvent]);
      }

      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Evento creado de manera correcta',
      });
    } catch (err) {
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'calendar',
          operation: LOG_OPERATIONS.UI_INTERACTION,
        },
        'Error in calendar operation',
        err instanceof Error ? err : new Error(String(err)),
      );
    }

    formikHelpers.resetForm();
    setShowCreateEvent(false);
  }

  async function onUpdateEventSubmit(
    values: tUpdateEventForm,
    formikHelpers: FormikHelpers<tUpdateEventForm>,
  ) {
    const calendarApi = calendarRef.current?.getApi();
    const calendarEvent = calendarApi?.getEventById(values.id);

    const formDate = getInterval({
      date: values.startDate.toISOString(),
      duration: values.duration,
      time: values.time.toISOString(),
    });

    const newEvent: EventInput = {
      id: values.id,
      title: values.title,
      color: '#EEF4FF',
      data: buildUpdatedEventData({
        values,
        guests: values.guests.map((guest) => guest.value),
        previousData: calendarEvent?.extendedProps?.data,
      }),
      allDay: false,
      start: formDate.startDate,
      end: formDate.endDate,
      resourceId: selectedEvent?.resourceId,
    };

    calendarEvent?.remove();
    calendarApi?.addEvent(newEvent);

    setEvents(
      events.map((event) =>
        event.id === newEvent.id ? Object.assign({}, event, newEvent) : event,
      ),
    );

    let updateEventRequest: Partial<tUpdateEventRequest> = {
      name: values.title,
      start_date: formDate.startDate,
      end_date: formDate.endDate,
      note: values.note,
      address: values.address,
      links: [values.link],
      description: values.description || '',
      guests: values.guests.map((guest) => guest.value),
    };

    const allowedFields = getAllowedFields(templates, values);
    updateEventRequest = removeEmpty(updateEventRequest);
    updateEventRequest = removeFields(updateEventRequest, allowedFields);

    try {
      await CalendarService.updateEvent(values.id, updateEventRequest);
    } catch (err) {
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'calendar',
          operation: LOG_OPERATIONS.UI_INTERACTION,
        },
        'Error in calendar operation',
        err instanceof Error ? err : new Error(String(err)),
      );
    }

    formikHelpers.resetForm();
    setShowUpdateEvent(false);
  }

  function onDeleteEventSubmit() {
    LLog.debug(
      {
        component: LOG_COMPONENTS.UI,
        method: 'Calendar.hook',
        operation: LOG_OPERATIONS.UI_INTERACTION,
      },
      'Eliminando evento del calendario',
      { selectedEvent },
    );
  }

  async function sendEventReminder(event: EventInput): Promise<void> {
    if (!event.id || !currentUser?.authId) return;

    return CalendarService.sendReminder(event.id, currentUser.authId);
  }

  return {
    calendarRef,
    events,
    setEvents,
    sendEventReminder,
    currentDate,
    currentDateTitle,
    setCurrentDate,
    setCurrentDateTitle,
    onNavigate,
    onEventClick,
    onViewResourceTimeGridDay,
    onChangeDatepicker,
    onToggleDatepicker,
    selectedEvent,
    setSelectedEvent,
    onCreateEventSubmit,
    onCreateChatEventSubmit,
    onUpdateEventSubmit,
    onDeleteEventSubmit,
    onDatesSetUpdate,
    changeView,
    currentView,
    showCreateEvent,
    setShowCreateEvent,
    showUpdateEvent,
    setShowUpdateEvent,
    showDeleteEvent,
    setShowDeleteEvent,
    showDeleteTemplate,
    setShowDeleteTemplate,
    templates,
    setTemplates,
    currentArg,
    setCurrentArg,
    currentTab,
    setCurrentTab,
    loading,
    setLoading,
    initialLoading,
    hasFetchedEvents,
    selectedTemplate,
    setSelectedTemplate,
  };
}
